import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { handleUnauthorized } from "../../utils/apiHelper";

// Doors dashboard (instrumentation Phase 5, 2026-08-14): the two numbers
// that together are the state of the business, on one card — gender ratio
// per pocket (live + 14-day trend) and the headline return-after-decline
// metric. Server does all math (/admin/doors-dashboard, definitions in the
// backend metrics module); this card only renders.

const pct = (v) => (v === null || v === undefined ? "–" : `${Math.round(v * 100)}%`);

const KIND_LABELS = {
  ended_by_me: "Ended it themselves",
  ended_by_them: "Other side ended it",
  expired_unrequited: "Chose, partner never did",
};

const DoorsDashboardCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/doors-dashboard`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setData(response.data || null);
      setError("");
    } catch (err) {
      if (err.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setError("Error loading doors dashboard");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, authToken, navigate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const headline = data?.headline;
  const pockets = (data?.pockets || []).filter((p) => !p.isReviewPocket);
  // Which population the activation gate is counting today. Older servers
  // do not send it; treat that as the raw roster, which is what they meant.
  const countingRule = data?.countingRule ?? "all_real_members";

  return (
    <div className="card mt-24">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h6 className="mb-0">Doors Dashboard</h6>
        <span className="text-sm text-neutral-500">
          {data?.windowDays ? `Decline window: last ${data.windowDays} days` : ""}
        </span>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-neutral-500">Loading…</div>
        ) : error ? (
          <div className="text-danger-600">{error}</div>
        ) : (
          <>
            {/* Headline: return after decline */}
            <div className="mb-24">
              <div className="d-flex align-items-baseline gap-3 flex-wrap">
                <span className="text-sm text-neutral-500">
                  Return after a reveal decline
                </span>
                <span className="fw-bold" style={{ fontSize: 28 }}>
                  {pct(headline?.d1Rate)} next day
                </span>
                <span className="fw-semibold" style={{ fontSize: 20 }}>
                  {pct(headline?.d7Rate)} within a week
                </span>
                <span className="text-sm text-neutral-500">
                  ({headline?.settledMoments ?? 0} settled moments
                  {headline?.unsettledMoments
                    ? `, ${headline.unsettledMoments} too recent to settle`
                    : ""}
                  {/* Closed by the janitor, not by a person: the Promise quiet
                      closes and the 6 PM unopened-card expiry. Named here
                      because these used to be counted AS declines, against
                      both members, which halved the number above. */}
                  {headline?.machineCloses
                    ? `, ${headline.machineCloses} closed quietly and not counted`
                    : ""}
                  )
                </span>
              </div>
              <div className="mt-8 d-flex gap-4 flex-wrap">
                {Object.entries(headline?.byKind || {}).map(([kind, k]) => (
                  <span key={kind} className="text-sm text-neutral-600">
                    {KIND_LABELS[kind] || kind}: {k.d1Returned}/{k.total} next day
                  </span>
                ))}
                {headline?.withdrawals?.total ? (
                  <span className="text-sm text-neutral-400">
                    (withdrawals, tracked separately: {headline.withdrawals.d1Returned}/
                    {headline.withdrawals.total} next day)
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-neutral-400 mt-4">
                {headline?.definition}
              </div>
            </div>

            {/* Pocket ratios */}
            <div className="table-responsive">
              <table className="table bordered-table mb-0">
                <thead>
                  <tr>
                    <th>Pocket</th>
                    <th>Status</th>
                    <th>Members</th>
                    <th>By gender</th>
                    <th>Balance ratio</th>
                    <th>14-day trend (all members)</th>
                  </tr>
                </thead>
                <tbody>
                  {pockets.map((p) => {
                    // Headline the population the gate actually evaluates, so
                    // this card and the Launch Pockets card never disagree.
                    // Falls back to the raw roster if the server predates the
                    // counted fields, or if the counting rule is set to count
                    // every real member.
                    const gatekeeperOnly = countingRule === "gatekeeper_complete";
                    const counted = gatekeeperOnly
                      ? p.countedMembers ?? p.realMembers
                      : p.realMembers;
                    const byGender = gatekeeperOnly
                      ? p.countedByGender ?? p.realByGender
                      : p.realByGender;
                    const balance = gatekeeperOnly
                      ? p.countedBalanceRatio ?? p.realBalanceRatio
                      : p.realBalanceRatio;
                    const target = p.activationThreshold?.total;
                    const excluded = (p.realMembers ?? 0) - (counted ?? 0);
                    return (
                      <tr key={p.id}>
                        <td>{p.name || p.slug}</td>
                        <td>{p.status}</td>
                        <td>
                          <div className="fw-semibold">
                            {counted}
                            {target ? ` / ${target}` : ""}
                          </div>
                          {excluded > 0 ? (
                            <div className="text-xs text-neutral-400">
                              {excluded} more signed up but never finished the
                              Gatekeeper
                            </div>
                          ) : null}
                        </td>
                        <td className="text-sm">
                          {Object.entries(byGender || {})
                            .map(([g, n]) => `${g}: ${n}`)
                            .join("  ·  ") || "–"}
                        </td>
                        <td className="fw-semibold">{pct(balance)}</td>
                        <td className="text-sm text-neutral-500">
                          {(p.trend || [])
                            .map((t) => pct(t.realBalanceRatio))
                            .join(" → ") || "no history yet"}
                        </td>
                      </tr>
                    );
                  })}
                  {!pockets.length && (
                    <tr>
                      <td colSpan={6} className="text-neutral-500">
                        No pockets yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="text-xs text-neutral-400 mt-2">
                {countingRule === "gatekeeper_complete"
                  ? "Members and balance count only people who finished the Gatekeeper, the same rule the hourly activation check uses, so this matches Launch Pockets. "
                  : "Members and balance count every real member, the same rule the hourly activation check uses. "}
                The trend is measured over all real members, because that is
                the only history we have; a trend on the stricter count starts
                from 2026-08-21.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DoorsDashboardCard;
